import { useEffect, useRef, useState, useCallback } from "react";
import AssetComponent from "components/core/asset/asset-component";
import classnames from "classnames";
import { trackEvent } from "services/bigbrain-service";
import { TAB_CLICKED, CAROUSEL_ARROW_CLICKED } from "constants/bigbrain-event-types";
import { StyledScrollableTabs } from "./scrollable-tabs.scss";
import DefaultProps from "./default-props";
import RightArrowIcon from "./right-arrow-icon";
import LeftArrowIcon from "./left-arrow-icon";
import MondayMarkupLanguageComponent from "client/components/system/monday-markup-language/monday-markup-language-component";
import { DUPLICATE_TABS_BY } from "./scrollable-tabs-consts";
import { getElementsInsideParent } from "./scrollable-tabs-utils";

const MINIMUM_SCROLLABLE_TABS = 3;

const ScrollableTabs = (props) => {
  const {
    tabs,
    defaultSelectedTabIndex,
    withDividerLine,
    selectedIconBackground,
    tabWidth,
    contentMarginTop,
    tabContentMaxWidth,
    iconSize
  } = props;
  const [selectedTab, setSelectedTab] = useState(tabs?.[defaultSelectedTabIndex]);
  const [tabsOffset, setTabsOffset] = useState(0);
  const [isSelctorLineTransition, setIsSelctorLineTransition] = useState(false);
  const [selectorLineOffset, setSelectorLineOffset] = useState(0);
  const [tabsScrollby, setTabsScrollby] = useState(0);
  const visibleTabsContainerRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const duplicateTabs = [].concat(...Array(DUPLICATE_TABS_BY).fill(tabs));

  // functions that are used in useEffect:

  const jumpSelectorLineToVisibleSelectedTabIFNeeded = useCallback(() => {
    /* this makes sure that the selector line "jumps" (moves without transition) to the last seen selected tab 
        so that when a new tab is selected the line moves over from the correct tab */
    const visibleSelectedTab = getFirstVisibleSelectedTag();
    if (visibleSelectedTab?.offsetLeft) {
      moveSelectorLine({ offsetLeft: visibleSelectedTab.offsetLeft, withTransition: false });
    }
  }, []);

  const recalculateAndSetTabsScrollby = useCallback(() => {
    // always scroll tabs by half of the number of visible tabs, minimum 3
    const tabsContainerOuterElement = visibleTabsContainerRef.current;
    const visibleTabs = Math.floor(tabsContainerOuterElement.offsetWidth / tabWidth);
    const newScrollby = Math.max(Math.floor(visibleTabs / 2), MINIMUM_SCROLLABLE_TABS);
    setTabsScrollby(newScrollby);
  }, [tabWidth]);

  const centerTabs = useCallback(() => {
    const visibleTabsContainerWidth = visibleTabsContainerRef.current.offsetWidth;
    const tabsContainerWidth = tabsContainerRef.current.offsetWidth;
    const newTabsOffset = Math.floor((visibleTabsContainerWidth - tabsContainerWidth) / 2);
    setTabsOffset(newTabsOffset);
  }, []);

  const onTabsTransitionEndHandler = useCallback(
    (e) => {
      if (e.propertyName === "transform") {
        jumpSelectorLineToVisibleSelectedTabIFNeeded();
      }
    },
    [jumpSelectorLineToVisibleSelectedTabIFNeeded]
  );

  //  useEffect:

  useEffect(() => {
    const tabsContainerElement = tabsContainerRef.current;

    window.addEventListener("resize", recalculateAndSetTabsScrollby);
    tabsContainerElement.addEventListener("transitionend", onTabsTransitionEndHandler);

    centerTabs();
    recalculateAndSetTabsScrollby();
    jumpSelectorLineToVisibleSelectedTabIFNeeded(); // move selector line to the first visible selected tab

    return () => {
      window.removeEventListener("resize", recalculateAndSetTabsScrollby);
      tabsContainerElement.removeEventListener("transitionend", onTabsTransitionEndHandler);
    };
  }, [
    onTabsTransitionEndHandler,
    centerTabs,
    recalculateAndSetTabsScrollby,
    jumpSelectorLineToVisibleSelectedTabIFNeeded
  ]);

  // event handlers:

  const onRightArrowClicked = () => {
    trackEvent(CAROUSEL_ARROW_CLICKED, {
      info3: "next"
    });
    setTabsOffset(tabsOffset - tabsScrollby * tabWidth);
  };

  const onLeftArrowClicked = () => {
    trackEvent(CAROUSEL_ARROW_CLICKED, {
      info3: "prev"
    });
    setTabsOffset(tabsOffset + tabsScrollby * tabWidth);
  };

  const onTabClicked = (e, tab) => {
    trackEvent(TAB_CLICKED, {
      kind: tab.label
    });
    const closestTab = e.target.closest(".tab");
    if (closestTab?.offsetLeft) {
      moveSelectorLine({ offsetLeft: closestTab.offsetLeft, withTransition: true });
    }
    setSelectedTab(tab);
  };

  // uitility functions:

  const moveSelectorLine = ({ offsetLeft, withTransition = true }) => {
    setIsSelctorLineTransition(withTransition);
    setSelectorLineOffset(offsetLeft);
  };

  const getFirstVisibleSelectedTag = () => {
    const visibleTabsContainerElement = tabsContainerRef.current;
    const selectedTabs = document.querySelectorAll(".selected");
    const visibleTab = getElementsInsideParent(selectedTabs, visibleTabsContainerElement)[0];
    return visibleTab;
  };

  return (
    <StyledScrollableTabs
      withDividerLine={withDividerLine}
      selectedIconBackground={selectedIconBackground}
      tabWidth={tabWidth}
      contentMarginTop={contentMarginTop}
      tabContentMaxWidth={tabContentMaxWidth}
      iconSize={iconSize}
    >
      <div className="scrollable-tabs">
        <div className="tabs-menu">
          <div className="arrow-container left-arrow-container" onClick={onLeftArrowClicked}>
            <LeftArrowIcon />
          </div>
          <div className="visible-tabs-container" ref={visibleTabsContainerRef}>
            <div className="tabs-container" ref={tabsContainerRef} style={{ transform: `translateX(${tabsOffset}px)` }}>
              <div className="tabs">
                {duplicateTabs?.map((tab, index) => {
                  const isSelected = tab?.label === selectedTab?.label;
                  return (
                    <div
                      key={index}
                      className={`tab ${isSelected ? "selected" : ""}`}
                      onClick={(e) => onTabClicked(e, tab)}
                    >
                      <div className="tab-icon-container">
                        <AssetComponent image={{ src: tab?.iconAsset }} className="tab-icon" />
                      </div>
                      <MondayMarkupLanguageComponent className="tab-label" text={tab?.label} />
                    </div>
                  );
                })}
              </div>
              {withDividerLine && (
                <div
                  className={classnames("tab-selector-line", { "with-transition": isSelctorLineTransition })}
                  style={{ "--selector-line-left": `${`${selectorLineOffset}`}px` }}
                >
                  <div className="tab-selector-line-color" />
                </div>
              )}
            </div>
          </div>
          <div className="arrow-container right-arrow-container" onClick={onRightArrowClicked}>
            <RightArrowIcon />
          </div>
        </div>
        {withDividerLine && <div className="tabs-divider-line" />}
        <div className="tab-content">
          {tabs?.map((tab, index) => (
            <div key={index} className={classnames("tab-content-container", { hidden: tab !== selectedTab })}>
              <AssetComponent {...tab.asset} className="tab-content-asset" />
            </div>
          ))}
        </div>
      </div>
    </StyledScrollableTabs>
  );
};

ScrollableTabs.defaultProps = DefaultProps;

export default ScrollableTabs;
